import React, { useRef, useEffect } from "react";
import {
  InnovationProductItem,
  InnovationProductListWrapper,
} from "../LandingInnovationAdrienCachot.styled";
import Typography from "../../../../../Foundation/Typography/Typography";
import StyledDiv from "../../../../StyledDiv/StyledDiv";
import { ILandingInnovationProductAdrienCachot } from "../LandingInnovationAdrienCachot";
import { lazyGsap } from "../../../../../lib/gsap/lazyGsap";

const marginTop1 = 152;
const marginTop2 = 106;

const desktopMarginTop: Record<number, number> = {
  0: marginTop1,
  1: 0,
  2: marginTop2,
  3: -marginTop1,
  4: -marginTop1 * 2,
  5: -(marginTop1 - marginTop2) * 6,
};

const columnStartY: Record<number, number> = {
  0: 10,
  1: 70,
  2: 40,
};

const columnEndY: Record<number, number> = {
  0: -120,
  1: -140,
  2: -80,
};

interface DesktopListAdrienCachotProps<T extends any = any> {
  title?: string;
  productList: ILandingInnovationProductAdrienCachot<T>[];
  itemRenderer: (
    product: ILandingInnovationProductAdrienCachot<T>,
    index: number
  ) => React.ReactNode;
}

const DesktopListAdrienCachot = <T extends any = any>({
  title,
  productList,
  itemRenderer,
}: DesktopListAdrienCachotProps<T>) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    let cancelled = false;
    let rafId: number | null = null;
    const tweens: any[] = [];
    let gsapRef: any = null;

    lazyGsap().then(({ gsap }) => {
      if (cancelled) return;
      gsapRef = gsap;
      rafId = requestAnimationFrame(() => {
        if (!wrapperRef.current) return;

        const triggerConfig = {
          trigger: wrapperRef.current,
          start: "top bottom",
          end: "bottom bottom",
          scrub: true,
        };

        const items = itemRefs.current.filter(
          (el): el is HTMLDivElement => !!el
        );

        items.forEach((item, index) => {
          tweens.push(
            gsap.fromTo(
              item,
              { yPercent: columnStartY[index % 3], immediateRender: true },
              {
                yPercent: columnEndY[index % 3],
                ease: "none",
                scrollTrigger: triggerConfig,
              }
            )
          );
        });
      });
    });

    return () => {
      cancelled = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
      tweens.forEach((t) => {
        t.scrollTrigger?.kill();
        t.kill();
      });
      if (gsapRef) {
        itemRefs.current.forEach((item) => {
          if (item) gsapRef.set(item, { clearProps: "transform" });
        });
      }
    };
  }, [productList]);

  return (
    <StyledDiv styles={{ zIndex: 3, width: "100%" }} ref={wrapperRef}>
      <InnovationProductListWrapper style={{ marginBottom: "-62%" }}>
        {productList.map((product, index) => {
          return (
            <InnovationProductItem
              key={product.id_product + "-" + index}
              style={{ marginTop: desktopMarginTop[index] }}
            >
              <div
                className="desktop-list-adrien-cachot-item"
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
              >
                {index === 0 && title && (
                  <StyledDiv styles={{ marginBottom: 162 }}>
                    <Typography variant="adriencachot" color="white">
                      {title}
                    </Typography>
                  </StyledDiv>
                )}
                {itemRenderer(product, index)}
              </div>
            </InnovationProductItem>
          );
        })}
      </InnovationProductListWrapper>
    </StyledDiv>
  );
};

export default DesktopListAdrienCachot;
